import React, { useState, useEffect, ReactNode, useContext, useRef } from 'react';
import { AuthContextType, User, UserRole } from '../types';
import { ToastContext } from './ToastContext';
import { supabase } from '../services/supabaseClient';
import type { Session } from '@supabase/supabase-js';

// Create AuthContext using the imported createContext function
export const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const toastContext = useContext(ToastContext);

  // Only warn once if the `last_login` column is missing from the DB schema.
  // This prevents spamming the console on every login when the column isn't present.
  const missingLastLoginWarningShown = useRef(false);

  // Helper to check if user is authenticated
  const isAuthenticated = !!user && !!session;

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        // Get initial session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        if (error) {
          console.error('Error getting session:', error);
          setIsLoading(false);
          return;
        }
        
        setSession(session);
        
        if (session?.user) {
          await loadUserProfile(session);
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error in initializeAuth:', error);
          setIsLoading(false);
        }
      }
    };

    // Set up auth state listener with debouncing
    let authChangeTimeout: NodeJS.Timeout;
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;
      
      console.log('Auth state changed:', event, session?.user?.email);
      
      // Debounce auth state changes to prevent rapid firing
      clearTimeout(authChangeTimeout);
      authChangeTimeout = setTimeout(async () => {
        if (!isMounted) return;
        
        setSession(session);
        
        if (session?.user) {
          await loadUserProfile(session);
        } else {
          setUser(null);
          setIsLoading(false);
        }
      }, 100); // 100ms debounce
    });

    // Initialize auth
    initializeAuth();

    return () => {
      isMounted = false;
      clearTimeout(authChangeTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const loadUserProfile = async (session: Session) => {
    console.log('🔄 Loading user profile for:', session.user.email);
    
    try {
      setIsLoading(true);

      // Add timeout protection
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Profile loading timeout')), 10000);
      });
      
      // First, check if profile exists in the profiles table
      const profilePromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
      
      const { data: profile, error } = await Promise.race([profilePromise, timeoutPromise]) as any;

      console.log('📊 Profile query result:', { profile, error });

      if (error && error.code !== 'PGRST116') {
        console.error('❌ Error loading profile:', error);
        
        // Fallback: create user from session data
        const fallbackUser = {
          id: session.user.id,
          email: session.user.email!,
          name: session.user.user_metadata?.name || session.user.email!.split('@')[0],
          created_at: session.user.created_at,
          updated_at: new Date().toISOString(),
          role: UserRole.USER, // Default role
        };
        
        console.log('🔄 Using fallback user data:', fallbackUser);
        setUser(fallbackUser);
        setIsLoading(false);
        return;
      }

      if (profile) {
        console.log('✅ Profile found, setting user:', profile);
        
        // Update last_login timestamp for successful login
        try {
          const { error: updateError } = await supabase
            .from('profiles')
            .update({ last_login: new Date().toISOString() })
            .eq('id', profile.id);

          if (updateError) {
            // If the DB schema doesn't have `last_login`, PostgREST returns PGRST204.
            // Warn once and provide actionable SQL to add the column. For other errors, log normally.
            if (
              updateError.code === 'PGRST204' ||
              (typeof updateError.message === 'string' && updateError.message.includes('last_login'))
            ) {
              if (!missingLastLoginWarningShown.current) {
                console.warn(
                  "⚠️ `last_login` column not found on `profiles`. To add it run (owner role / Supabase SQL editor):\n\nALTER TABLE public.profiles ADD COLUMN last_login timestamptz;\n\nThis message will be suppressed after the first warning."
                );
                missingLastLoginWarningShown.current = true;
              }
            } else {
              console.warn('⚠️ Could not update last_login:', updateError);
            }
          } else {
            console.log('✅ Updated last_login for user:', profile.email);
          }
        } catch (error) {
          console.warn('⚠️ Error updating last_login:', error);
        }
        
        // Profile exists, use it
        setUser({
          id: profile.id,
          email: profile.email,
          name: profile.name,
          created_at: profile.created_at,
          updated_at: profile.updated_at,
          role: profile.role || UserRole.USER, // Default to 'user' if not in profile
          subscription_end_date: profile.subscription_end_date,
        });
      } else {
        console.log('🆕 No profile found, creating new one...');
        // Profile doesn't exist, create one
        const newProfileData = {
          id: session.user.id,
          email: session.user.email!,
          name: session.user.user_metadata?.name || session.user.email!.split('@')[0],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          role: UserRole.USER, // Default role for new users
        };
        
        console.log('📝 Creating profile with data:', newProfileData);
        
        const { data: newProfile, error: insertError } = await supabase
          .from('profiles')
          .insert(newProfileData)
          .select()
          .single();

        if (insertError) {
          console.error('❌ Error creating profile:', insertError);
          
          // Fallback: use session data even if DB insert fails
          const fallbackUser = {
            id: session.user.id,
            email: session.user.email!,
            name: session.user.user_metadata?.name || session.user.email!.split('@')[0],
            created_at: session.user.created_at,
            updated_at: new Date().toISOString(),
            role: UserRole.USER, // Default role
          };
          
          console.log('🔄 Using fallback user data after insert error:', fallbackUser);
          setUser(fallbackUser);
        } else {
          console.log('✅ Profile created successfully:', newProfile);
          setUser({
            id: newProfile.id,
            email: newProfile.email,
            name: newProfile.name,
            created_at: newProfile.created_at,
            updated_at: newProfile.updated_at,
            role: newProfile.role || UserRole.USER, // Default to 'user' if not in newProfile
            subscription_end_date: newProfile.subscription_end_date,
          });
        }
      }
    } catch (error) {
      console.error('❌ Error in loadUserProfile:', error);
      
      // Final fallback: create user from session data
      const fallbackUser = {
        id: session.user.id,
        email: session.user.email!,
        name: session.user.user_metadata?.name || session.user.email!.split('@')[0],
        created_at: session.user.created_at,
        updated_at: new Date().toISOString(),
        role: UserRole.USER, // Default role
      };
      
      console.log('🔄 Using final fallback user data:', fallbackUser);
      setUser(fallbackUser);
    } finally {
      console.log('🔚 Setting isLoading to false');
      setIsLoading(false);
    }
  };

  const signIn = async (email: string, password: string): Promise<void> => {
    try {
      console.log('🔐 Starting sign in process for email:', email);
      setIsLoading(true);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      console.log('🔐 Sign in response:', { data, error });

      if (error) {
        console.error('🔐 Sign in error:', error);
        throw error;
      }

      console.log('🔐 Sign in successful, user:', data.user?.email);
      toastContext?.showToast('success', 'Successfully signed in!');
    } catch (error) {
      console.error('🔐 Sign in caught error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Sign in failed';
      toastContext?.showToast('error', errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (name: string, email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
          },
        },
      });

      if (error) {
        throw error;
      }

      toastContext?.showToast('success', 'Account created successfully! Please check your email to verify your account.');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sign up failed';
      toastContext?.showToast('error', errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        throw error;
      }

      setUser(null);
      setSession(null);
      toastContext?.showToast('success', 'Successfully signed out!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sign out failed';
      toastContext?.showToast('error', errorMessage);
      throw error;
    }
  };

  const signInWithGoogle = async (): Promise<void> => {
    try {
      // Determine the correct redirect URL based on environment
      const getRedirectUrl = () => {
        const origin = window.location.origin;
        
        console.log('🔍 Current origin:', origin);
        console.log('🔍 Current hostname:', window.location.hostname);
        console.log('🔍 Current port:', window.location.port);
        
        // Always use the current origin for the callback
        const redirectUrl = `${origin}/auth/callback`;
        
        console.log('🔍 Generated redirect URL:', redirectUrl);
        return redirectUrl;
      };

      const redirectUrl = getRedirectUrl();
      console.log('🔐 Starting Google OAuth with redirect URL:', redirectUrl);
      console.log('🔐 Supabase will redirect to this URL after OAuth processing');

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        console.error('🔐 Google OAuth error:', error);
        throw error;
      }
    } catch (error) {
      console.error('🔐 Google sign in error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Google sign in failed';
      toastContext?.showToast('error', errorMessage);
      throw error;
    }
  };

  const resetPassword = async (email: string): Promise<void> => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) {
        throw error;
      }

      toastContext?.showToast('success', 'Password reset instructions sent to your email');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Password reset failed';
      toastContext?.showToast('error', errorMessage);
      throw error;
    }
  };

  const updateProfile = async (data: { name: string; email: string }): Promise<void> => {
    try {
      if (!user) throw new Error('No user logged in');

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          name: data.name,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (profileError) {
        throw profileError;
      }

      // Update email if provided and different
      if (data.email && data.email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: data.email,
        });

        if (emailError) {
          throw emailError;
        }
      }

      // Refresh user profile
      if (session) {
        await loadUserProfile(session);
      }
      
      toastContext?.showToast('success', 'Profile updated successfully!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Profile update failed';
      toastContext?.showToast('error', errorMessage);
      throw error;
    }
  };

  const updatePassword = async (newPassword: string): Promise<void> => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        throw error;
      }

      toastContext?.showToast('success', 'Password updated successfully!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Password update failed';
      toastContext?.showToast('error', errorMessage);
      throw error;
    }
  };

  const deleteAccount = async (): Promise<void> => {
    try {
      if (!user) throw new Error('No user logged in');

      // Delete profile first
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', user.id);

      if (profileError) {
        throw profileError;
      }

      // Sign out user
      await signOut();
      
      toastContext?.showToast('success', 'Account deleted successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Account deletion failed';
      toastContext?.showToast('error', errorMessage);
      throw error;
    }
  };

  const forceRefreshProfile = async (): Promise<void> => {
    try {
      if (session) {
        await loadUserProfile(session);
      }
    } catch (error) {
      console.error('Error refreshing user profile:', error);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    updateProfile,
    updatePassword,
    resetPassword,
    deleteAccount,
    forceRefreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
